import axios from "axios";
import { useEffect, useState, useRef } from "react";
import {
  FiBriefcase,
  FiCheckCircle,
  FiCopy,
  FiDollarSign,
  FiEye,
  FiMapPin,
  FiSearch,
  FiShare2,
  FiUsers,
  FiX,
  FiUserPlus,
  FiMail,
  FiSend,
  FiCalendar,
  FiClock,
  FiAward,
  FiUserCheck,
  FiFilter,
  FiDownload,
  FiTrendingUp,
  FiCheckSquare,
  FiRefreshCw,
  FiAlertCircle,
  FiInfo,
  FiLink,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiPlus,
  FiClock as FiClockIcon,
  FiStar,
  FiFlag,
  FiZap,
  FiActivity,
  FiShield,
  FiKey,
  FiLayers,
  FiClipboard,
  FiEdit
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../index.css";
import "./EmployeeDashboard.css";

const API_BASE = API_BASE_URL;

function AdminEmployeeReferral() {
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [referralMessage, setReferralMessage] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const [error, setError] = useState(null);
  const [referralSuccess, setReferralSuccess] = useState(false);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState("");
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // State for referral settings
  const [referralSettings, setReferralSettings] = useState({
    expiryDate: "",
    maxReferralsPerEmployee: 5,
    referralStatus: "pending"
  });

  // State for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingReferral, setEditingReferral] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editBonus, setEditBonus] = useState(0);
  const [editNote, setEditNote] = useState("");

  // State for view modal
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingReferral, setViewingReferral] = useState(null);

  // State for delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const clientId = localStorage.getItem('clientId');
  const adminId = localStorage.getItem('adminId') || localStorage.getItem('userId');
  const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch jobs and employees
  useEffect(() => {
    if (clientId) {
      fetchJobs();
      fetchEmployees();
      fetchAssignedJobs();
    } else {
      setError("Please login to access referrals");
      setLoading(false);
    }

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsEmployeeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [clientId]);

  const getTodayDateString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getMinExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getMaxExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 90);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getFullJobLink = (jobLink) => {
    if (!jobLink) return '';
    const cleanLink = jobLink.startsWith('/') ? jobLink : `/${jobLink}`;
    return `${window.location.origin}${cleanLink}`;
  };

  const getInitial = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API_BASE}/jobs/all?clientId=${clientId}`);
      if (response.data.success) {
        const allJobs = response.data.jobPosts || [];
        setJobs(allJobs);
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
    }
  };

  const fetchEmployees = async () => {
    try {
      setFetchingEmployees(true);
      
      const headers = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await axios.get(
        "https://api.timelyhealth.in/api/employees/get-employees",
        { headers }
      );
      
      let employeesData = [];
      if (Array.isArray(response.data)) {
        employeesData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        employeesData = response.data.data;
      } else if (response.data.employees && Array.isArray(response.data.employees)) {
        employeesData = response.data.employees;
      }
      
      setAllEmployees(employeesData);
      const activeEmployees = employeesData.filter(emp => emp.status === 'active');
      setEmployees(activeEmployees);
      
    } catch (err) {
      console.error("Error fetching employees:", err);
      const mockEmployees = [
        { _id: '68f5ceb33d14a37fb9db1ec8', employeeId: 'TH001', name: 'G NARESH KUMAR', email: 'nareshyadav5313@gmail.com', department: 'Laboratory Medicine', role: 'Phlebotomist', status: 'active' },
        { _id: 'emp2', employeeId: 'TH002', name: 'Priya Patel', email: 'priya.patel@company.com', department: 'Marketing', role: 'Marketing Manager', status: 'active' },
        { _id: 'emp3', employeeId: 'TH003', name: 'Rajesh Kumar', email: 'rajesh.kumar@company.com', department: 'Sales', role: 'Sales Executive', status: 'active' },
        { _id: 'emp4', employeeId: 'TH004', name: 'Sneha Reddy', email: 'sneha.reddy@company.com', department: 'HR', role: 'HR Specialist', status: 'active' },
        { _id: 'emp5', employeeId: 'TH005', name: 'Vikram Singh', email: 'vikram.singh@company.com', department: 'Engineering', role: 'DevOps Engineer', status: 'active' },
      ];
      setAllEmployees(mockEmployees);
      setEmployees(mockEmployees);
    } finally {
      setFetchingEmployees(false);
    }
  };

  // GET ALL ASSIGNED REFERRALS
  const fetchAssignedJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`https://ingrainhirebackend.ingrainsystems.com/api/jobs/allassignedreffraljobs`);
      
      console.log("📦 Assigned referrals response:", response.data);
      
      if (response.data.success) {
        setAssignedJobs(response.data.data || []);
      } else {
        setError(response.data.message || "Failed to fetch assigned referrals");
        setAssignedJobs([]);
      }
    } catch (err) {
      console.error("❌ Error fetching assigned jobs:", err);
      setError(err.response?.data?.message || "Network error. Please try again.");
      setAssignedJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // ASSIGN REFERRAL TO EMPLOYEES
  const handleSendReferrals = async () => {
    if (selectedEmployees.length === 0) {
      toast.warning("Please select at least one employee to refer");
      return;
    }

    if (!referralSettings.expiryDate) {
      toast.warning("Please set an expiry date for the referral");
      return;
    }

    const expiryDateObj = new Date(referralSettings.expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (expiryDateObj < today) {
      toast.warning("Expiry date must be in the future");
      return;
    }

    setIsSending(true);
    try {
      const fullJobLink = getFullJobLink(selectedJob.link);
      
      const payload = {
        jobId: selectedJob._id,
        jobTitle: selectedJob.role,
        jobLink: fullJobLink,
        employees: selectedEmployees.map(emp => ({
          id: emp.employeeId || emp._id,
          email: emp.email,
          name: emp.name
        })),
        message: referralMessage,
        adminId: adminId,
        clientId: clientId,
        expiryDate: referralSettings.expiryDate,
        maxReferralsPerEmployee: referralSettings.maxReferralsPerEmployee,
        status: referralSettings.referralStatus
      };

      const response = await axios.post(`https://ingrainhirebackend.ingrainsystems.com/api/jobs/assignjobforreffraltoemp`, payload);

      if (response.data.success) {
        toast.success(`Job assigned to ${selectedEmployees.length} employee(s) for referral!`);
        setReferralSuccess(true);
        setTimeout(() => {
          setIsReferralModalOpen(false);
          setReferralSuccess(false);
          setSelectedEmployees([]);
          fetchAssignedJobs();
        }, 2000);
      } else {
        toast.error(response.data.message || "Failed to assign referrals");
      }
    } catch (err) {
      console.error("Error assigning referrals:", err);
      toast.error(err.response?.data?.message || "Failed to assign. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  // UPDATE REFERRAL STATUS
  const handleUpdateStatus = async (referralId, newStatus, note = "") => {
    try {
      const response = await axios.put(`https://ingrainhirebackend.ingrainsystems.com/api/jobs/updatestatusforassignedreffral/${referralId}`, {
        status: newStatus,
        note: note
      });

      if (response.data.success) {
        toast.success(`Referral status updated to ${newStatus}!`);
        fetchAssignedJobs();
        setIsEditModalOpen(false);
        setEditingReferral(null);
      } else {
        toast.error(response.data.message || "Failed to update status");
      }
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  // DELETE REFERRAL
  const handleDeleteReferral = async (referralId) => {
    try {
      const response = await axios.delete(`https://ingrainhirebackend.ingrainsystems.com/api/jobs/deleteassignedreffral/${referralId}`);

      if (response.data.success) {
        toast.success("Referral deleted successfully!");
        fetchAssignedJobs();
        setDeleteConfirmId(null);
      } else {
        toast.error(response.data.message || "Failed to delete referral");
      }
    } catch (err) {
      console.error("Error deleting referral:", err);
      toast.error(err.response?.data?.message || "Failed to delete referral");
    }
  };

  const openViewModal = (referral) => {
    setViewingReferral(referral);
    setIsViewModalOpen(true);
  };

  const openEditModal = (referral) => {
    setEditingReferral(referral);
    setEditStatus(referral.status || 'pending');
    setEditBonus(referral.bonusAmount || 0);
    setEditNote("");
    setIsEditModalOpen(true);
  };

  const openReferralModal = () => {
    setSelectedJob(null);
    setSelectedEmployees([]);
    setEmployeeSearch("");
    
    const defaultExpiry = new Date();
    defaultExpiry.setDate(defaultExpiry.getDate() + 30);
    const yyyy = defaultExpiry.getFullYear();
    const mm = String(defaultExpiry.getMonth() + 1).padStart(2, '0');
    const dd = String(defaultExpiry.getDate()).padStart(2, '0');
    
    setReferralSettings({
      expiryDate: `${yyyy}-${mm}-${dd}`,
      maxReferralsPerEmployee: 5,
      referralStatus: "pending"
    });
    
    setReferralMessage(`Dear Team,\n\nI am excited to share an excellent opportunity at our company. This role offers great growth potential and aligns perfectly with our team's goals.\n\nI believe some of you might be a great fit or know someone who would be. Please review the details below and let me know if you're interested or have any referrals.\n\nLooking forward to your response!\n\nBest regards,\nHR Team`);
    setIsReferralModalOpen(true);
    setIsEmployeeDropdownOpen(false);
  };

  const toggleEmployeeSelection = (employee) => {
    setSelectedEmployees(prev => {
      const exists = prev.find(emp => emp.employeeId === employee.employeeId);
      if (exists) {
        return prev.filter(emp => emp.employeeId !== employee.employeeId);
      } else {
        return [...prev, employee];
      }
    });
  };

  const getFilteredEmployees = () => {
    let filtered = employees;

    if (employeeSearch) {
      const search = employeeSearch.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.name?.toLowerCase().includes(search) ||
        emp.email?.toLowerCase().includes(search) ||
        (emp.employeeId && emp.employeeId.toLowerCase().includes(search)) ||
        (emp.department && emp.department.toLowerCase().includes(search)) ||
        (emp.role && emp.role.toLowerCase().includes(search))
      );
    }

    if (selectedDepartmentFilter) {
      filtered = filtered.filter(emp => emp.department === selectedDepartmentFilter);
    }

    return filtered;
  };

  const departments = [...new Set(employees.map(emp => emp.department).filter(Boolean))];

  // Filter assigned jobs
  const filteredAssignedJobs = assignedJobs.filter(referral => {
    if (!debouncedSearch) return true;
    const search = debouncedSearch.toLowerCase();
    return referral.jobTitle?.toLowerCase().includes(search) ||
      referral.employeeName?.toLowerCase().includes(search) ||
      referral.employeeEmail?.toLowerCase().includes(search);
  });

  // Filter by status
  const statusFilteredJobs = selectedStatusFilter 
    ? filteredAssignedJobs.filter(r => r.status === selectedStatusFilter)
    : filteredAssignedJobs;

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-50 text-yellow-600 border-yellow-200';
      case 'active': return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 'expired': return 'bg-red-50 text-red-600 border-red-200';
      case 'completed': return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'cancelled': return 'bg-gray-50 text-gray-600 border-gray-200';
      default: return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return <FiClockIcon className="text-yellow-500" size={12} />;
      case 'active': return <FiCheckCircle className="text-emerald-500" size={12} />;
      case 'expired': return <FiX className="text-red-500" size={12} />;
      case 'completed': return <FiCheck className="text-blue-500" size={12} />;
      case 'cancelled': return <FiX className="text-gray-500" size={12} />;
      default: return <FiInfo className="text-gray-500" size={12} />;
    }
  };

  // Get stats
  const totalAssigned = assignedJobs.length;
  const pendingCount = assignedJobs.filter(r => r.status === 'pending').length;
  const activeCount = assignedJobs.filter(r => r.status === 'active').length;
  const completedCount = assignedJobs.filter(r => r.status === 'completed').length;
  const expiredCount = assignedJobs.filter(r => r.status === 'expired').length;

  if (!clientId) {
    return (
      <div className="w-full min-h-screen p-2 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-indigo-50 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Please login to access admin referral panel</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "white" }} className="emp-dash">
      <ToastContainer />
      <main>
        {/* Page Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              <span>Referral</span>
            </h1>
            <p className="emp-dash__subtitle">
              Manage employee referrals and assign job opportunities
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="emp-dash__date-pill">
              <FiShield />
              <span>
                {new Date().toLocaleDateString("en-US", {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
            <button
              type="button"
              onClick={openReferralModal}
              className="flex items-center gap-2 py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider transition-all shadow-lg shadow-indigo-600/20 cursor-pointer"
            >
              <FiPlus size={15} /> Assign Job
            </button>
          </div>
        </div>

        {/* Stats Strip */}
        <div className="emp-dash__stats grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Total Assigned</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--indigo">
                <FiCheckSquare />
              </div>
            </div>
            <div className="emp-dash__stat-value">{totalAssigned}</div>
            <div className="emp-dash__stat-meta">referrals assigned</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Pending</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--yellow">
                <FiClockIcon />
              </div>
            </div>
            <div className="emp-dash__stat-value">{pendingCount}</div>
            <div className="emp-dash__stat-meta">awaiting action</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Active</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--emerald">
                <FiCheckCircle />
              </div>
            </div>
            <div className="emp-dash__stat-value">{activeCount}</div>
            <div className="emp-dash__stat-meta">in progress</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Completed</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--purple">
                <FiStar />
              </div>
            </div>
            <div className="emp-dash__stat-value">{completedCount}</div>
            <div className="emp-dash__stat-meta">successful referrals</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Expired</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rose">
                <FiFlag />
              </div>
            </div>
            <div className="emp-dash__stat-value">{expiredCount}</div>
            <div className="emp-dash__stat-meta">past deadline</div>
          </div>
        </div>

        {/* Main Card */}
        <div className="emp-dash__card p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                <FiLink size={15} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Referral Directory
                </h2>
                <p className="text-[10px] text-gray-500">
                  Showing {statusFilteredJobs.length} of {assignedJobs.length} records
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  fetchAssignedJobs();
                  toast.success("Referrals refreshed!");
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 text-[10px] font-bold uppercase tracking-widest border border-gray-200 transition-all cursor-pointer"
              >
                <FiRefreshCw size={12} className={loading ? "animate-spin" : ""} />
                Sync
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-5">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-600 transition-colors z-10"
              size={14}
            />
            <input
              type="text"
              placeholder="Search by employee name, email, or job title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 focus:bg-white focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-xs text-gray-800"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FiX size={14} />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="mb-4 flex flex-wrap gap-2">
            <select
              value={selectedStatusFilter}
              onChange={(e) => setSelectedStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-xl border border-gray-200/80">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50/90 border-b border-gray-200 text-gray-600 uppercase text-[10px] tracking-wider font-bold">
                  <th className="py-3 px-4 w-12 text-center">#</th>
                  <th className="py-3 px-4">Employee</th>
                  <th className="py-3 px-4">Job</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Expiry</th>
                  <th className="py-3 px-4">Assigned Date</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center text-gray-400">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-6 h-6 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                        <span className="text-xs font-medium">Loading referrals...</span>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 rounded-xl bg-red-50 text-red-400 flex items-center justify-center mb-2">
                          <FiAlertCircle size={24} />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          {error}
                        </p>
                        <button
                          onClick={fetchAssignedJobs}
                          className="mt-3 px-4 py-2 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          Retry
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : statusFilteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-300 flex items-center justify-center mb-2">
                          <FiUsers size={24} />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          No Referrals Found
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {searchQuery
                            ? "Try matching different keywords."
                            : "Click 'Assign Job' above to create a referral."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  statusFilteredJobs.map((referral, index) => (
                    <tr key={referral._id} className="hover:bg-indigo-50/20 transition-colors">
                      <td className="py-3.5 px-4 text-center text-gray-400 font-medium text-[11px]">
                        {index + 1}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 shadow-sm"
                            style={{
                              background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                            }}
                          >
                            {getInitial(referral.employeeName)}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 text-xs">
                              {referral.employeeName}
                            </p>
                            <p className="text-[10px] text-gray-500 font-medium">
                              {referral.employeeEmail}
                            </p>
                            <span className="text-[9px] text-gray-400">ID: {referral.employeeId || 'N/A'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <p className="font-medium text-gray-800 text-xs">{referral.jobTitle}</p>
                        <p className="text-[10px] text-blue-500 truncate max-w-[150px]">{referral.jobLink}</p>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-medium ${getStatusBadgeColor(referral.status)}`}>
                          {getStatusIcon(referral.status)} {referral.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="text-[11px] text-gray-600">
                          {referral.expiryDate ? new Date(referral.expiryDate).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="text-[11px] text-gray-500">
                          {referral.assignedAt ? new Date(referral.assignedAt).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => openViewModal(referral)}
                            className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                            title="View Details"
                          >
                            <FiEye size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditModal(referral)}
                            className="p-1.5 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors cursor-pointer"
                            title="Edit Status"
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirmId(referral._id)}
                            className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                            title="Delete"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Tip */}
        <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 flex items-start gap-3">
          <FiZap size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wider mb-1">
              Referral Management Tips
            </h4>
            <p className="text-[11px] text-amber-800 leading-relaxed">
              Assign jobs to employees who can refer qualified candidates. Each referral has an expiry date and max referral limit. Track status and bonus payments here.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest py-8">
          <FiClipboard className="text-indigo-500" />
          Last Updated: {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </div>
      </main>

      {/* ============================================= */}
      {/* ASSIGN REFERRAL MODAL */}
      {/* ============================================= */}
      {isReferralModalOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 via-white to-violet-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-600/20">
                  <FiUserPlus size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Assign Job to Employee</h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">
                    Create New Referral Assignment
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsReferralModalOpen(false)}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
              {/* Select Job */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                  Select Job <span className="text-rose-500">*</span>
                </label>
                <select
                  value={selectedJob?._id || ""}
                  onChange={(e) => {
                    const job = jobs.find(j => j._id === e.target.value);
                    setSelectedJob(job);
                    if (job) {
                      setReferralMessage(`Dear Team,\n\nI am excited to share an excellent opportunity at our company for the ${job.role} position. This role offers great growth potential and aligns perfectly with our team's goals.\n\nI believe some of you might be a great fit or know someone who would be. Please review the details below and let me know if you're interested or have any referrals.\n\nLooking forward to your response!\n\nBest regards,\nHR Team`);
                    }
                  }}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-xs"
                >
                  <option value="">Select a job...</option>
                  {jobs.map(job => (
                    <option key={job._id} value={job._id}>
                      {job.role} - {job.location || 'Remote'}
                    </option>
                  ))}
                </select>
                {jobs.length === 0 && (
                  <p className="text-[10px] text-amber-600 mt-1">No jobs available. Please create a job first.</p>
                )}
              </div>

              {/* Selected Job Preview */}
              {selectedJob && (
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                  <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">Selected Job</p>
                  <h4 className="text-sm font-semibold text-gray-800">{selectedJob.role}</h4>
                  <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                    {selectedJob.location && <span>📍 {selectedJob.location}</span>}
                    {selectedJob.salary && <span>💰 {selectedJob.salary}</span>}
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[10px] text-blue-600 truncate">
                    <FiLink className="text-xs" />
                    <span className="truncate">{getFullJobLink(selectedJob.link)}</span>
                  </div>
                </div>
              )}

              {/* Select Employees */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                  Select Employees <span className="text-rose-500">*</span>
                </label>
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsEmployeeDropdownOpen((prev) => !prev)}
                    className="w-full px-4 py-2.5 text-left bg-white border border-gray-200 rounded-xl hover:border-indigo-500 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all flex items-center justify-between"
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {selectedEmployees.length > 0 ? (
                        selectedEmployees.map(emp => (
                          <span key={emp.employeeId || emp._id} className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] rounded-full flex items-center gap-1 font-bold">
                            {emp.name} ({emp.employeeId || 'N/A'})
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleEmployeeSelection(emp);
                              }}
                              className="hover:text-indigo-900"
                            >
                              <FiX className="text-xs" />
                            </button>
                          </span>
                        ))
                      ) : (
                        <span className="text-[11px] text-gray-400 font-medium">Select employees to assign...</span>
                      )}
                    </div>
                    <FiUsers className={`text-gray-400 transition-transform ${isEmployeeDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isEmployeeDropdownOpen && (
                    <div className="absolute left-0 z-50 mt-1.5 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-56 overflow-y-auto">
                      <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
                        <div className="relative">
                          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                          <input
                            type="text"
                            placeholder="Search by name, email or ID..."
                            value={employeeSearch}
                            onChange={(e) => setEmployeeSearch(e.target.value)}
                            className="w-full py-1.5 pl-8 pr-3 text-xs bg-gray-50 border border-gray-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="p-1">
                        {getFilteredEmployees().length === 0 ? (
                          <div className="p-3 text-center text-xs text-gray-400">
                            No active employees found
                          </div>
                        ) : (
                          getFilteredEmployees().map(emp => (
                            <div
                              key={emp.employeeId || emp._id}
                              onClick={() => toggleEmployeeSelection(emp)}
                              className={`p-2.5 rounded-lg cursor-pointer flex items-center justify-between text-xs transition-colors ${selectedEmployees.find(e => e.employeeId === emp.employeeId)
                                  ? "bg-indigo-50 text-indigo-700 font-bold"
                                  : "hover:bg-gray-50 text-gray-700"
                                }`}
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div
                                  className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0"
                                  style={{
                                    background: emp.profileImage
                                      ? `url(${emp.profileImage}) center/cover`
                                      : "linear-gradient(135deg, #4f46e5, #7c3aed)",
                                  }}
                                >
                                  {!emp.profileImage && getInitial(emp.name)}
                                </div>
                                <div className="truncate">
                                  <p className="font-bold text-gray-800 truncate">{emp.name}</p>
                                  <p className="text-[10px] text-gray-400 truncate">ID: {emp.employeeId || 'N/A'}</p>
                                </div>
                              </div>
                              {emp.department && (
                                <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                  {emp.department}
                                </span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  {selectedEmployees.length} employee(s) selected
                </p>
              </div>

              {/* Referral Settings */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider">
                    <FiCalendar className="inline mr-1 text-gray-400" />
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={referralSettings.expiryDate}
                    onChange={(e) => setReferralSettings(prev => ({ ...prev, expiryDate: e.target.value }))}
                    min={getMinExpiryDate()}
                    max={getMaxExpiryDate()}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-xs"
                  />
                  <p className="text-[9px] text-gray-400">Min: 7 days • Max: 90 days</p>
                </div>
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider">
                    <FiUsers className="inline mr-1 text-gray-400" />
                    Max Referrals/Employee
                  </label>
                  <select
                    value={referralSettings.maxReferralsPerEmployee}
                    onChange={(e) => setReferralSettings(prev => ({ ...prev, maxReferralsPerEmployee: Number(e.target.value) }))}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-xs"
                  >
                    <option value={1}>1 referral</option>
                    <option value={3}>3 referrals</option>
                    <option value={5}>5 referrals</option>
                    <option value={10}>10 referrals</option>
                    <option value={20}>20 referrals</option>
                    <option value={50}>50 referrals</option>
                    <option value={100}>Unlimited</option>
                  </select>
                </div>
              </div>

              {/* Status Selector */}
              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider">
                  <FiInfo className="inline mr-1 text-gray-400" />
                  Referral Status
                </label>
                <select
                  value={referralSettings.referralStatus}
                  onChange={(e) => setReferralSettings(prev => ({ ...prev, referralStatus: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-xs"
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              {/* Message */}
              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider">
                  Message to Employees
                </label>
                <textarea
                  value={referralMessage}
                  onChange={(e) => setReferralMessage(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-xs resize-none"
                  placeholder="Write a message to employees about this opportunity..."
                />
              </div>

              {/* Summary */}
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1">
                  <FiInfo className="text-sm" />
                  Assignment Summary
                </p>
                <div className="grid grid-cols-3 gap-2 mt-1 text-xs text-gray-600">
                  <div>
                    <span className="text-gray-400">Employees:</span>
                    <span className="font-medium ml-1">{selectedEmployees.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Expires:</span>
                    <span className="font-medium ml-1">
                      {referralSettings.expiryDate ? new Date(referralSettings.expiryDate).toLocaleDateString() : 'Not set'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Max/Employee:</span>
                    <span className="font-medium ml-1">{referralSettings.maxReferralsPerEmployee}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsReferralModalOpen(false)}
                className="py-2.5 px-5 rounded-xl border border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendReferrals}
                disabled={isSending || selectedEmployees.length === 0 || !selectedJob}
                className="py-2.5 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-50 flex items-center gap-2 cursor-pointer"
              >
                {isSending ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <FiCheck size={14} />
                    Assign to {selectedEmployees.length}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* VIEW REFERRAL MODAL */}
      {/* ============================================= */}
      {isViewModalOpen && viewingReferral && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 via-white to-violet-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-md shadow-indigo-600/20 flex-shrink-0"
                  style={{
                    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                  }}
                >
                  {getInitial(viewingReferral.employeeName)}
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Referral Details</h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">
                    #{viewingReferral._id?.slice(-6) || 'N/A'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsViewModalOpen(false)}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
              {/* Employee Info */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Employee</p>
                <div className="flex items-center gap-3 mt-1">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{
                      background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                    }}
                  >
                    {getInitial(viewingReferral.employeeName)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{viewingReferral.employeeName}</p>
                    <p className="text-xs text-gray-500">{viewingReferral.employeeEmail}</p>
                    <p className="text-[10px] text-gray-400">Employee ID: {viewingReferral.employeeId || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Job Info */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Job</p>
                <p className="font-semibold text-gray-800 mt-1">{viewingReferral.jobTitle}</p>
                <div className="mt-1 flex items-center gap-1 text-xs text-blue-600 truncate">
                  <FiLink className="text-xs" />
                  <span className="truncate">{viewingReferral.jobLink}</span>
                </div>
              </div>

              {/* Status & Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</p>
                  <span className={`mt-1 inline-block px-2 py-1 rounded-full text-[10px] font-medium ${getStatusBadgeColor(viewingReferral.status)}`}>
                    {getStatusIcon(viewingReferral.status)} {viewingReferral.status?.toUpperCase() || 'PENDING'}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Max Referrals</p>
                  <p className="text-lg font-bold text-gray-800 mt-1">{viewingReferral.maxReferralsPerEmployee || 5}</p>
                </div>
              </div>

              {/* Expiry & Assigned Date */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Expiry Date</p>
                  <p className="text-sm font-medium text-gray-700 mt-1">
                    {viewingReferral.expiryDate ? new Date(viewingReferral.expiryDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Assigned Date</p>
                  <p className="text-sm font-medium text-gray-700 mt-1">
                    {viewingReferral.assignedAt ? new Date(viewingReferral.assignedAt).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Bonus */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Bonus</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-lg font-bold text-emerald-600">₹{viewingReferral.bonusAmount || 0}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${viewingReferral.isBonusPaid ? 'bg-emerald-50 text-emerald-600' : 'bg-yellow-50 text-yellow-600'}`}>
                    {viewingReferral.isBonusPaid ? 'Paid' : 'Pending'}
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 text-center">
                  <p className="text-[10px] text-gray-400">Total</p>
                  <p className="text-lg font-bold text-gray-800">{viewingReferral.totalReferralsMade || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 text-center">
                  <p className="text-[10px] text-gray-400">Successful</p>
                  <p className="text-lg font-bold text-emerald-600">{viewingReferral.successfulReferrals || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 text-center">
                  <p className="text-[10px] text-gray-400">Pending</p>
                  <p className="text-lg font-bold text-yellow-600">
                    {(viewingReferral.totalReferralsMade || 0) - (viewingReferral.successfulReferrals || 0)}
                  </p>
                </div>
              </div>

              {/* Message */}
              {viewingReferral.message && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Message</p>
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{viewingReferral.message}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setIsViewModalOpen(false);
                  openEditModal(viewingReferral);
                }}
                className="py-2 px-4 rounded-xl bg-yellow-50 text-yellow-700 hover:bg-yellow-100 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5"
              >
                <FiEdit size={13} /> Edit
              </button>
              <button
                type="button"
                onClick={() => setIsViewModalOpen(false)}
                className="py-2 px-5 rounded-xl border border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* EDIT STATUS MODAL */}
      {/* ============================================= */}
      {isEditModalOpen && editingReferral && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-5 bg-gradient-to-r from-amber-50 via-white to-yellow-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-600 text-white flex items-center justify-center shadow-md shadow-amber-600/20">
                  <FiEdit2 size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Update Referral</h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
                    {editingReferral.employeeName} • {editingReferral.jobTitle}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <FiX size={16} />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider">
                  <FiInfo className="inline mr-1 text-gray-400" />
                  Status
                </label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:border-amber-600 focus:ring-4 focus:ring-amber-600/5 transition-all outline-none text-xs"
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider">
                  <FiAward className="inline mr-1 text-gray-400" />
                  Bonus Amount (₹)
                </label>
                <input
                  type="number"
                  value={editBonus}
                  onChange={(e) => setEditBonus(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:border-amber-600 focus:ring-4 focus:ring-amber-600/5 transition-all outline-none text-xs"
                  placeholder="0"
                  min="0"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider">
                  Note
                </label>
                <textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:border-amber-600 focus:ring-4 focus:ring-amber-600/5 transition-all outline-none text-xs resize-none"
                  placeholder="Add a note about this status change..."
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="py-2.5 px-5 rounded-xl border border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleUpdateStatus(editingReferral._id, editStatus, editNote)}
                className="py-2.5 px-6 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-wider transition-all shadow-lg shadow-amber-600/20 flex items-center gap-2 cursor-pointer"
              >
                <FiCheck size={14} />
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ============================================= */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[350] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center space-y-4 border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
              <FiTrash2 size={22} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Delete Referral?</h3>
              <p className="text-xs text-gray-500 mt-1">
                This action cannot be undone. The referral will be permanently removed.
              </p>
            </div>
            <div className="flex gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteReferral(deleteConfirmId)}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-wider transition-colors shadow-md shadow-rose-600/20 cursor-pointer"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminEmployeeReferral;